'use client';

import { useState, useRef, DragEvent } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useI18n } from '@/contexts/I18nContext';

interface Props {
  value: string[];
  onChange: (v: string[]) => void;
  max?: number;
  label?: string;
  hint?: string;
  kind?: 'image' | 'video' | 'audio';
}

export function ReferenceImages({
  value,
  onChange,
  max = 5,
  label,
  hint,
  kind = 'image',
}: Props) {
  const { t } = useI18n();
  const resolvedLabel =
    label ??
    (kind === 'video'
      ? t('studio.generator.referenceVideos')
      : kind === 'audio'
      ? t('studio.generator.referenceAudios')
      : t('studio.generator.referenceImages'));
  const resolvedHint = hint ?? t('studio.reference.defaultHint');
  const accept =
    kind === 'video'
      ? 'video/*'
      : kind === 'audio'
      ? 'audio/*'
      : 'image/*';
  const { authedFetch } = useAuthToken();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlInput, setUrlInput] = useState('');

  async function uploadFiles(files: File[]) {
    setError(null);
    const remaining = max - value.length;
    const toUpload = files.slice(0, remaining);
    if (!toUpload.length) return;

    setUploading(true);
    const uploaded: string[] = [];
    for (const f of toUpload) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await authedFetch('/api/ai/wavespeed/upload', {
          method: 'POST',
          body: fd,
        });
        const json = await res.json();
        if (json.success && json.data?.url) {
          uploaded.push(json.data.url);
        } else {
          setError(json.error || t('studio.reference.uploadFailed'));
        }
      } catch (e: any) {
        setError(e?.message || t('studio.reference.uploadFailed'));
      }
    }
    if (uploaded.length) onChange([...value, ...uploaded]);
    setUploading(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const prefix =
      kind === 'video' ? 'video/' : kind === 'audio' ? 'audio/' : 'image/';
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith(prefix)
    );
    if (files.length) uploadFiles(files);
  }

  function addUrl() {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    if (value.length >= max) return;
    onChange([...value, trimmed]);
    setUrlInput('');
  }

  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <section className="mb-4">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] tracking-wide text-white/60 uppercase">
          {resolvedLabel}
        </label>
        <span className="text-[10px] text-white/60">
          {value.length}/{max}
        </span>
      </div>

      {/* Thumbnails grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-5 gap-1.5 mb-2">
          {value.map((url, i) => (
            <div
              key={i}
              className="group relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-white/5"
            >
              {kind === 'image' && (
                <img src={url} alt="" className="w-full h-full object-cover" />
              )}
              {kind === 'video' && (
                <video
                  src={url}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
              )}
              {kind === 'audio' && (
                <div className="flex h-full w-full items-center justify-center break-all p-1 text-[10px] text-white/70">
                  🎵 {(url.split('/').pop() || '').slice(0, 14)}
                </div>
              )}
              <button
                onClick={() => remove(i)}
                className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white opacity-0 transition group-hover:opacity-100"
                title={t('studio.reference.remove')}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone + URL input */}
      {value.length < max && (
        <>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-3 py-4 transition ${
              dragOver
                ? 'border-fuchsia-400/60 bg-fuchsia-500/15 text-white'
                : 'border-white/15 bg-white/5 text-white/70 hover:border-white/30 hover:bg-white/10'
            }`}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            <span className="text-center text-[10px]">
              {uploading ? t('studio.reference.uploading') : t('studio.reference.drop')}
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length) uploadFiles(files);
              e.target.value = '';
            }}
          />

          <div className="flex gap-1 mt-1.5">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addUrl()}
              placeholder={t('studio.reference.pasteUrl')}
              className="flex-1 rounded-xl border border-white/15 bg-white/10 px-2 py-1.5 text-[11px] text-white outline-none transition placeholder:text-white/40 focus:border-fuchsia-400/60 focus:ring-2 focus:ring-fuchsia-500/20"
            />
            <button
              type="button"
              onClick={addUrl}
              disabled={!urlInput.trim()}
              className="rounded-xl bg-brand-500 px-2 py-1.5 text-[11px] font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
            >
              {t('studio.reference.add')}
            </button>
          </div>
        </>
      )}

      {resolvedHint && <p className="mt-1.5 text-[10px] text-white/60">{resolvedHint}</p>}
      {error && (
        <p className="text-[10px] text-red-300 mt-1.5">{error}</p>
      )}
    </section>
  );
}
